import React, { useState } from 'react'
import { View, Text } from 'react-native'
import { Link, useLocalSearchParams, useRouter } from 'expo-router'
import { apiPost } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { KeyboardAwareScrollView } from '../../components/ui/KeyboardAwareScrollView'
import { useToast } from '../../components/ui/Toast'
import type { ResetPasswordRequest } from '@warcarp/api-types'

export default function ResetPasswordScreen() {
  const { token } = useLocalSearchParams<{ token: string }>()
  const router = useRouter()
  const { showToast } = useToast()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [confirmError, setConfirmError] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  if (!token) {
    return (
      <View className="flex-1 bg-canvas justify-center px-6 gap-4">
        <Text className="text-ink text-2xl font-stencil uppercase">Invalid Link</Text>
        <Text className="text-ink-soft font-typed">This reset link is missing a token. Please request a new one.</Text>
        <Link href="/(auth)/forgot-password" className="text-blood font-semibold">
          Request new link
        </Link>
      </View>
    )
  }

  async function handleSubmit() {
    let valid = true
    setPasswordError('')
    setConfirmError('')

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      valid = false
    }
    if (password !== confirm) {
      setConfirmError('Passwords do not match')
      valid = false
    }
    if (!valid) return

    setLoading(true)
    try {
      await apiPost<void>('/api/v1/auth/reset-password', { token, password } satisfies ResetPasswordRequest)
      setDone(true)
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Reset failed. The link may have expired.', 'error')
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <View className="flex-1 bg-canvas justify-center px-6 gap-6">
        <View className="gap-3">
          <Text className="text-4xl">✓</Text>
          <Text className="text-ink text-2xl font-stencil uppercase">Password updated</Text>
          <Text className="text-ink-soft font-typed">Your password has been reset. You can now sign in.</Text>
        </View>
        <Button title="Sign In" onPress={() => router.replace('/(auth)/login')} />
      </View>
    )
  }

  return (
    <KeyboardAwareScrollView
      className="flex-1 bg-canvas"
      contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', paddingHorizontal: 24, gap: 32 }}
    >
      <View className="gap-2">
        <Text className="text-2xl text-ink font-stencil uppercase">New Password</Text>
        <Text className="text-ink-soft font-typed">Enter a new password for your account.</Text>
      </View>
      <View className="gap-4">
        <Input
          label="New Password"
          value={password}
          onChangeText={(v) => { setPassword(v); setPasswordError('') }}
          secureTextEntry
          autoComplete="new-password"
          error={passwordError}
          // Enter / Return submits from either field, like an HTML form on desktop.
          onSubmitEditing={() => { if (!loading) void handleSubmit() }}
        />
        <Input
          label="Confirm Password"
          value={confirm}
          onChangeText={(v) => { setConfirm(v); setConfirmError('') }}
          secureTextEntry
          autoComplete="new-password"
          error={confirmError}
          returnKeyType="go"
          onSubmitEditing={() => { if (!loading) void handleSubmit() }}
        />
      </View>
      <Button title="Reset Password" onPress={handleSubmit} loading={loading} />
      <Link href="/(auth)/login" className="text-ink-soft text-center font-typed">
        Back to Sign In
      </Link>
    </KeyboardAwareScrollView>
  )
}
